// WbVatSettingsPage — /weighbridge/settings/vat.
//
// Phase C5. Two sections on one page:
//   A) Company VAT registration — vat_enabled + vat_registration_number
//      + default_vat_rate_id. Save sends ONLY these three fields against
//      PATCH /wb/company-settings (the rest of company-settings is owned
//      by the Compliance page in C6 — we must not echo it back).
//   B) VAT rates list — inline-edit table over /wb/vat-rates. Tenant rows
//      can be edited, added, or deleted; global rows (is_global=true) are
//      rendered read-only with their action buttons disabled.
//
// The list is non-paginated and small (≤ a couple of dozen rows in
// practice), so we render the full list with no virtualisation. Inline
// editing keeps the user on a single screen instead of routing into a
// detail page — there is no GET /vat-rates/:enc_id endpoint anyway, so a
// detail page would have nothing to fetch.

import { useEffect, useState } from 'react';
import { Lock, Pencil, Plus, Trash2 } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import { ConfirmModal } from '../../../components/ui/ConfirmModal/ConfirmModal';
import { useToastStore } from '../../../store/toastStore';
import { useWbCompanySettings } from '../context/WbCompanySettingsContext';
import { useUpdateWbCompanySettings } from '../hooks/useWbCompanySettings';
import {
  useCreateVatRate,
  useDeleteVatRate,
  useUpdateVatRate,
  useWbVatRates,
  type WbVatRate,
} from '../hooks/useWbVatRates';
import { extractErrorMessage } from '../../../utils/errorMessage';
import styles from './WbVatSettingsPage.module.css';

interface SectionAState {
  vat_enabled: boolean;
  vat_registration_number: string;
  default_vat_rate_id: string;
}

interface RowEditState {
  name: string;
  percentage: string;
  is_exempt: boolean;
  is_default: boolean;
}

interface NewRowState extends RowEditState {
  code: string;
}

const EMPTY_NEW_ROW: NewRowState = {
  name: '',
  code: '',
  percentage: '',
  is_exempt: false,
  is_default: false,
};

export function WbVatSettingsPage(): JSX.Element {
  const settingsCtx = useWbCompanySettings();
  const settings = settingsCtx.settings;
  const updateSettings = useUpdateWbCompanySettings();
  const pushToast = useToastStore((s) => s.push);

  // Section A form — mirrors the three VAT fields on company-settings.
  const [sectionA, setSectionA] = useState<SectionAState>({
    vat_enabled: false,
    vat_registration_number: '',
    default_vat_rate_id: '',
  });
  const [sectionAErrors, setSectionAErrors] = useState<Record<string, string>>(
    {},
  );
  const [sectionAError, setSectionAError] = useState<string | null>(null);

  // Hydrate Section A once settings load.
  useEffect(() => {
    if (!settings) return;
    setSectionA({
      vat_enabled: settings.vat_enabled,
      vat_registration_number: settings.vat_registration_number ?? '',
      default_vat_rate_id: settings.default_vat_rate?.enc_id ?? '',
    });
  }, [settings]);

  // VAT rates list + per-row edit state.
  const ratesQuery = useWbVatRates();
  const rates = ratesQuery.data?.vat_rates ?? [];

  const [editingRowEncId, setEditingRowEncId] = useState<string | null>(null);
  const [rowEdit, setRowEdit] = useState<RowEditState>({
    name: '',
    percentage: '',
    is_exempt: false,
    is_default: false,
  });
  const [rowError, setRowError] = useState<string | null>(null);

  const [adding, setAdding] = useState(false);
  const [newRow, setNewRow] = useState<NewRowState>(EMPTY_NEW_ROW);
  const [newRowError, setNewRowError] = useState<string | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<WbVatRate | null>(null);

  const createRateMut = useCreateVatRate();
  const updateRateMut = useUpdateVatRate(editingRowEncId ?? '');
  const deleteRateMut = useDeleteVatRate(deleteTarget?.enc_id ?? '');

  // -- Section A handlers ---------------------------------------------------

  function validateSectionA(): boolean {
    const errs: Record<string, string> = {};
    if (sectionA.vat_enabled) {
      if (sectionA.vat_registration_number.trim() === '') {
        errs['vat_registration_number'] =
          'VAT registration number is required when VAT is enabled.';
      } else if (sectionA.vat_registration_number.length > 20) {
        errs['vat_registration_number'] =
          'VAT registration number must be 20 characters or fewer.';
      }
    }
    setSectionAErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function handleSaveSectionA() {
    setSectionAError(null);
    if (!validateSectionA()) return;
    // Partial PATCH — only the three VAT fields. The C6 (compliance) page
    // owns the rest of company-settings; we must never echo those fields
    // back here.
    const body = {
      vat_enabled: sectionA.vat_enabled,
      vat_registration_number: sectionA.vat_enabled
        ? sectionA.vat_registration_number.trim()
        : null,
      default_vat_rate_id: sectionA.vat_enabled
        ? sectionA.default_vat_rate_id === ''
          ? null
          : sectionA.default_vat_rate_id
        : null,
    };
    updateSettings.mutate(body, {
      onSuccess: () => {
        settingsCtx.refetch();
        pushToast({ type: 'success', message: 'VAT settings saved.' });
      },
      onError: (err) => setSectionAError(extractErrorMessage(err)),
    });
  }

  // -- Section B (rates table) handlers -------------------------------------

  function beginRowEdit(rate: WbVatRate) {
    if (rate.is_global) return; // defensive — UI gate disables the button
    setEditingRowEncId(rate.enc_id);
    setRowEdit({
      name: rate.name,
      percentage: String(rate.percentage),
      is_exempt: rate.is_exempt,
      is_default: rate.is_default,
    });
    setRowError(null);
  }

  function cancelRowEdit() {
    setEditingRowEncId(null);
    setRowError(null);
  }

  function saveRowEdit() {
    setRowError(null);
    if (!editingRowEncId) return;
    if (rowEdit.name.trim() === '') {
      setRowError('Name is required.');
      return;
    }
    const pct = parseFloat(rowEdit.percentage);
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
      setRowError('Percentage must be between 0 and 100.');
      return;
    }
    updateRateMut.mutate(
      {
        name: rowEdit.name.trim(),
        percentage: pct,
        is_exempt: rowEdit.is_exempt,
        is_default: rowEdit.is_default,
      },
      {
        onSuccess: () => {
          setEditingRowEncId(null);
          pushToast({ type: 'success', message: 'VAT rate updated.' });
        },
        onError: (err) => setRowError(extractErrorMessage(err)),
      },
    );
  }

  function saveNewRow() {
    setNewRowError(null);
    if (newRow.name.trim() === '') {
      setNewRowError('Name is required.');
      return;
    }
    if (newRow.code.trim() === '') {
      setNewRowError('Code is required.');
      return;
    }
    if (newRow.code.length > 10) {
      setNewRowError('Code must be 10 characters or fewer.');
      return;
    }
    const pct = parseFloat(newRow.percentage);
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
      setNewRowError('Percentage must be between 0 and 100.');
      return;
    }
    createRateMut.mutate(
      {
        name: newRow.name.trim(),
        code: newRow.code.trim(),
        percentage: pct,
        is_exempt: newRow.is_exempt,
        is_default: newRow.is_default,
      },
      {
        onSuccess: () => {
          setAdding(false);
          setNewRow(EMPTY_NEW_ROW);
          pushToast({ type: 'success', message: 'VAT rate created.' });
        },
        onError: (err) => setNewRowError(extractErrorMessage(err)),
      },
    );
  }

  function handleDeleteConfirm() {
    if (!deleteTarget) return;
    deleteRateMut.mutate(undefined, {
      onSuccess: () => {
        setDeleteTarget(null);
        pushToast({ type: 'success', message: 'VAT rate deleted.' });
      },
      onError: (err) => {
        // Surface 409 (in-use) and 403 (global row) as a toast; close the
        // modal either way so the user can re-try.
        setDeleteTarget(null);
        pushToast({ type: 'error', message: extractErrorMessage(err) });
      },
    });
  }

  // -- Render ---------------------------------------------------------------

  // Active tenant + global rates can be selected as default. We filter
  // inactive rows out of the dropdown to keep the picker tidy.
  const dropdownOptions = rates.filter((r) => r.is_active);

  return (
    <div className={styles.page}>
      <PageHeader
        title="VAT Settings"
        subtitle="Company VAT registration and tenant VAT rates"
      />

      {/* -- Section A: company VAT registration --------------------------- */}
      <Card>
        {settingsCtx.isLoading ? (
          <div className={styles.tableLoading}>
            <Spinner label="Loading settings" />
          </div>
        ) : settingsCtx.isError ? (
          <p className={styles.error}>Could not load company settings.</p>
        ) : (
          <div className={styles.section}>
            <h3 className={styles.sectionTitle}>Registration</h3>
            <label className={styles.toggleRow}>
              <input
                type="checkbox"
                checked={sectionA.vat_enabled}
                onChange={(e) =>
                  setSectionA((prev) => ({
                    ...prev,
                    vat_enabled: e.target.checked,
                  }))
                }
              />
              <span>VAT registered</span>
            </label>
            <p className={styles.sectionHelp}>
              Enable to charge VAT on applicable products.
            </p>

            {sectionA.vat_enabled ? (
              <div className={styles.grid}>
                <label className={styles.field}>
                  <span>VAT registration number</span>
                  <input
                    type="text"
                    maxLength={20}
                    value={sectionA.vat_registration_number}
                    onChange={(e) =>
                      setSectionA((prev) => ({
                        ...prev,
                        vat_registration_number: e.target.value,
                      }))
                    }
                  />
                  {sectionAErrors['vat_registration_number'] ? (
                    <span className={styles.fieldError}>
                      {sectionAErrors['vat_registration_number']}
                    </span>
                  ) : null}
                </label>

                <label className={styles.field}>
                  <span>Default VAT rate</span>
                  <select
                    value={sectionA.default_vat_rate_id}
                    onChange={(e) =>
                      setSectionA((prev) => ({
                        ...prev,
                        default_vat_rate_id: e.target.value,
                      }))
                    }
                    disabled={ratesQuery.isLoading}
                  >
                    <option value="">— None —</option>
                    {dropdownOptions.map((r) => (
                      <option key={r.enc_id} value={r.enc_id}>
                        {r.name} ({r.percentage}%)
                      </option>
                    ))}
                  </select>
                  <span className={styles.fieldHelp}>
                    Applied to new products that don&apos;t specify their own
                    rate.
                  </span>
                </label>
              </div>
            ) : null}

            {sectionAError ? (
              <p className={styles.error}>{sectionAError}</p>
            ) : null}

            <div className={styles.actions}>
              <Button
                type="button"
                variant="primary"
                onClick={handleSaveSectionA}
                loading={updateSettings.isPending}
              >
                Save VAT settings
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* -- Section B: VAT rates list ------------------------------------- */}
      <Card>
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>VAT Rates</h3>
          <p className={styles.sectionHelp}>
            Global rates (marked below) are managed by Tricivo and cannot be
            edited.
          </p>

          {ratesQuery.isLoading ? (
            <div className={styles.tableLoading}>
              <Spinner label="Loading VAT rates" />
            </div>
          ) : (
            <>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Code</th>
                    <th>Rate (%)</th>
                    <th>Exempt</th>
                    <th>Default</th>
                    <th>Global</th>
                    <th>Active</th>
                    <th aria-label="Actions"></th>
                  </tr>
                </thead>
                <tbody>
                  {rates.length === 0 ? (
                    <tr>
                      <td
                        colSpan={8}
                        style={{ textAlign: 'center', padding: '1.5rem' }}
                      >
                        No VAT rates configured.
                      </td>
                    </tr>
                  ) : null}

                  {rates.map((rate) => {
                    const isEditing = editingRowEncId === rate.enc_id;
                    const isLocked = rate.is_global;
                    return (
                      <tr
                        key={rate.enc_id}
                        className={isLocked ? styles.globalRow : ''}
                      >
                        {isEditing ? (
                          <>
                            <td>
                              <input
                                type="text"
                                aria-label="Name"
                                value={rowEdit.name}
                                onChange={(e) =>
                                  setRowEdit((prev) => ({
                                    ...prev,
                                    name: e.target.value,
                                  }))
                                }
                              />
                            </td>
                            <td>{rate.code}</td>
                            <td>
                              <input
                                type="number"
                                aria-label="Percentage"
                                min={0}
                                max={100}
                                step="0.01"
                                value={rowEdit.percentage}
                                onChange={(e) =>
                                  setRowEdit((prev) => ({
                                    ...prev,
                                    percentage: e.target.value,
                                  }))
                                }
                              />
                            </td>
                            <td>
                              <input
                                type="checkbox"
                                aria-label="Exempt"
                                checked={rowEdit.is_exempt}
                                onChange={(e) =>
                                  setRowEdit((prev) => ({
                                    ...prev,
                                    is_exempt: e.target.checked,
                                  }))
                                }
                              />
                            </td>
                            <td>
                              <input
                                type="checkbox"
                                aria-label="Default"
                                checked={rowEdit.is_default}
                                onChange={(e) =>
                                  setRowEdit((prev) => ({
                                    ...prev,
                                    is_default: e.target.checked,
                                  }))
                                }
                              />
                            </td>
                            <td>No</td>
                            <td>{rate.is_active ? 'Yes' : 'No'}</td>
                            <td>
                              <div className={styles.rowActions}>
                                <Button
                                  type="button"
                                  variant="primary"
                                  size="sm"
                                  onClick={saveRowEdit}
                                  loading={updateRateMut.isPending}
                                >
                                  Save
                                </Button>
                                <Button
                                  type="button"
                                  variant="secondary"
                                  size="sm"
                                  onClick={cancelRowEdit}
                                >
                                  Cancel
                                </Button>
                              </div>
                            </td>
                          </>
                        ) : (
                          <>
                            <td>{rate.name}</td>
                            <td>{rate.code}</td>
                            <td>{rate.percentage}</td>
                            <td>{rate.is_exempt ? 'Yes' : 'No'}</td>
                            <td>{rate.is_default ? 'Yes' : 'No'}</td>
                            <td>
                              {rate.is_global ? (
                                <span className={styles.tagPill}>
                                  <Lock size={12} /> Global
                                </span>
                              ) : (
                                'No'
                              )}
                            </td>
                            <td>{rate.is_active ? 'Yes' : 'No'}</td>
                            <td>
                              <div className={styles.rowActions}>
                                <Button
                                  type="button"
                                  variant="secondary"
                                  size="sm"
                                  icon={Pencil}
                                  onClick={() => beginRowEdit(rate)}
                                  disabled={isLocked}
                                  title={
                                    isLocked
                                      ? 'Global rates are managed by Tricivo.'
                                      : undefined
                                  }
                                >
                                  Edit
                                </Button>
                                <Button
                                  type="button"
                                  variant="danger"
                                  size="sm"
                                  icon={Trash2}
                                  onClick={() => setDeleteTarget(rate)}
                                  disabled={isLocked || rate.is_default}
                                  title={
                                    isLocked
                                      ? 'Global rates are managed by Tricivo.'
                                      : rate.is_default
                                        ? 'Clear the default flag before deleting.'
                                        : undefined
                                  }
                                >
                                  Delete
                                </Button>
                              </div>
                            </td>
                          </>
                        )}
                      </tr>
                    );
                  })}

                  {adding ? (
                    <tr>
                      <td>
                        <input
                          type="text"
                          aria-label="New rate name"
                          value={newRow.name}
                          onChange={(e) =>
                            setNewRow((prev) => ({
                              ...prev,
                              name: e.target.value,
                            }))
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          aria-label="New rate code"
                          maxLength={10}
                          value={newRow.code}
                          onChange={(e) =>
                            setNewRow((prev) => ({
                              ...prev,
                              code: e.target.value,
                            }))
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          aria-label="New rate percentage"
                          min={0}
                          max={100}
                          step="0.01"
                          value={newRow.percentage}
                          onChange={(e) =>
                            setNewRow((prev) => ({
                              ...prev,
                              percentage: e.target.value,
                            }))
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          aria-label="New rate exempt"
                          checked={newRow.is_exempt}
                          onChange={(e) =>
                            setNewRow((prev) => ({
                              ...prev,
                              is_exempt: e.target.checked,
                            }))
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          aria-label="New rate default"
                          checked={newRow.is_default}
                          onChange={(e) =>
                            setNewRow((prev) => ({
                              ...prev,
                              is_default: e.target.checked,
                            }))
                          }
                        />
                      </td>
                      <td>No</td>
                      <td>Yes</td>
                      <td>
                        <div className={styles.rowActions}>
                          <Button
                            type="button"
                            variant="primary"
                            size="sm"
                            onClick={saveNewRow}
                            loading={createRateMut.isPending}
                          >
                            Add
                          </Button>
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                              setAdding(false);
                              setNewRow(EMPTY_NEW_ROW);
                              setNewRowError(null);
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>

              {rowError ? <p className={styles.error}>{rowError}</p> : null}
              {newRowError ? (
                <p className={styles.error}>{newRowError}</p>
              ) : null}

              {!adding ? (
                <div>
                  <Button
                    type="button"
                    variant="secondary"
                    icon={Plus}
                    onClick={() => {
                      setAdding(true);
                      setNewRow(EMPTY_NEW_ROW);
                      setNewRowError(null);
                    }}
                    className={styles.addRowButton}
                  >
                    Add VAT Rate
                  </Button>
                </div>
              ) : null}
            </>
          )}
        </div>
      </Card>

      <ConfirmModal
        open={deleteTarget !== null}
        title="Delete VAT rate?"
        message={
          <p>
            Delete <strong>{deleteTarget?.name ?? ''}</strong>? This cannot be
            undone.
          </p>
        }
        variant="danger"
        confirmLabel="Delete"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
        loading={deleteRateMut.isPending}
      />
    </div>
  );
}
